/**
 * login-form.tsx — Form đăng nhập dùng chung cho trang /login (hộp nổi trên nền hệ thống) và hộp "Phiên đã hết hạn"
 * hiện ngay trên trang đang làm việc. Email + mật khẩu (ẩn/hiện), tick đồng ý Điều khoản/Chính sách (ghi bằng
 * chứng mỗi lần đăng nhập — NĐ 356/2025/NĐ-CP Điều 6), "Đặt lại mật khẩu", nút Đăng nhập, "hoặc" + nút Google
 * tròn (khi đã cấu hình). Khi bật Cloudflare Turnstile: ô xác minh bảo mật trên nút Đăng nhập.
 * "Duy trì đăng nhập" không còn hỏi ở đây — mặc định BẬT cho mọi phiên đăng nhập bằng form này (ô tick cũ đã
 * nhường chỗ cho tick đồng ý điều khoản, giá trị cao hơn cho đa số người dùng bình thường).
 */
import { useCallback, useState, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { queryClient } from '@/lib/query-client'
import { captchaPendingMessage } from '@/lib/turnstile'
import { TurnstileBox, useTurnstile } from '@/components/shared/turnstile-box'
import { useAuthStore } from '@/stores/auth-store'
import type { AuthUser, TotpChallenge } from '@/types/api'
import { AUTH_INPUT } from './auth-layout'
import { authApi, loginErrorMessage, resolveGoogleClientId, useSignupConfig } from './auth-api'
import { SocialRow } from './google-button'
import { PasswordInput } from './password-field'
import { TotpLoginStep } from './totp-login-step'
import { Trans, useTranslation } from 'react-i18next'

/** Luôn bật — "Duy trì đăng nhập" không còn là lựa chọn hiển thị ở màn đăng nhập (xem ghi chú đầu file). */
const ALWAYS_REMEMBER = true

export interface GoogleSignupTicket {
  signupToken: string
  email: string
  fullName: string
}

export function LoginForm({ initialEmail = '', notice, forgotLink, onLoggedIn, onGoogleSignup }: {
  initialEmail?: string
  /** Thông báo (vd. lý do bị đăng xuất) hiện trên form. */
  notice?: string | null
  /** Link/nút "Đặt lại mật khẩu". */
  forgotLink: ReactNode
  onLoggedIn: (user: AuthUser) => void
  /** Email Google chưa có tài khoản. Không truyền → báo lỗi ngay trên form. */
  onGoogleSignup?: (ticket: GoogleSignupTicket) => void
}) {
  const { t } = useTranslation('auth')
  const login = useAuthStore((s) => s.login)
  const applySession = useAuthStore((s) => s.applySession)
  const completeTotpLogin = useAuthStore((s) => s.completeTotpLogin)
  const startTotpLoginSetup = useAuthStore((s) => s.startTotpLoginSetup)
  const confirmTotpLoginSetup = useAuthStore((s) => s.confirmTotpLoginSetup)
  const { config } = useSignupConfig()
  const ts = useTurnstile(config?.turnstileSiteKey, 'login')
  const termsUrl = config?.termsUrl || '/dieu-khoan'
  const privacyUrl = config?.privacyUrl || '/chinh-sach-bao-mat'

  const [email, setEmail] = useState(initialEmail)
  const [password, setPassword] = useState('')
  const [agreedTerms, setAgreedTerms] = useState(false)
  const [showTermsConfirm, setShowTermsConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Mật khẩu đúng nhưng còn thiếu bước xác thực 2 lớp (xem backend auth-service.ts#LoginOutcome).
  const [totpChallenge, setTotpChallenge] = useState<TotpChallenge | null>(null)

  const doLogin = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Quyền của phiên trước (người khác đăng nhập cùng trình duyệt) không được dùng lại để chọn trang đầu.
      queryClient.removeQueries({ queryKey: ['me', 'permissions'] })
      const result = await login(email.trim(), password, ALWAYS_REMEMBER, ts.token, true)
      if ('totpRequired' in result) setTotpChallenge(result)
      else onLoggedIn(result)
    } catch (err) {
      setError(loginErrorMessage(err))
      ts.reset() // token Turnstile dùng được một lần — sai mật khẩu cũng đã tiêu token
    } finally {
      setLoading(false)
    }
  }, [email, login, onLoggedIn, password, ts])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (ts.pending) return setError(captchaPendingMessage())
    // Chưa tick đồng ý điều khoản → hỏi lại bằng popup thay vì chặn im lặng; xác nhận trong popup thì cho qua.
    if (!agreedTerms) return setShowTermsConfirm(true)
    await doLogin()
  }

  const onConfirmTerms = useCallback(async () => {
    setAgreedTerms(true)
    setShowTermsConfirm(false)
    await doLogin()
  }, [doLogin])

  const onGoogle = useCallback(async (credential: string) => {
    setError(null)
    try {
      const r = await authApi.google(credential)
      if (r.status === 'totp_required') {
        setTotpChallenge({ totpRequired: true, mode: r.mode, ticket: r.ticket })
        return
      }
      if (r.status === 'logged_in') {
        queryClient.removeQueries({ queryKey: ['me', 'permissions'] })
        onLoggedIn(applySession(r, ALWAYS_REMEMBER))
        return
      }
      if (onGoogleSignup) onGoogleSignup({ signupToken: r.signupToken, ...r.profile })
      else setError(t('login.googleNotFound', { email: r.profile.email }))
    } catch (err) {
      setError(loginErrorMessage(err))
    }
  }, [applySession, onGoogleSignup, onLoggedIn, t])

  if (totpChallenge) {
    return (
      <TotpLoginStep
        challenge={totpChallenge}
        onVerify={(ticket, code) => completeTotpLogin(ticket, code, ALWAYS_REMEMBER)}
        onSetupStart={startTotpLoginSetup}
        onSetupConfirm={(ticket, code) => confirmTotpLoginSetup(ticket, code, ALWAYS_REMEMBER)}
        onDone={onLoggedIn}
        onBack={() => setTotpChallenge(null)}
        errorMessage={loginErrorMessage}
      />
    )
  }

  return (
    <>
      <form onSubmit={onSubmit} className="space-y-4">
        {notice && (
          <p className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm" role="status">{notice}</p>
        )}
        <div>
          <Label htmlFor="email" className="sr-only">Email</Label>
          <Input
            id="email"
            type="email"
            className={AUTH_INPUT}
            autoComplete="username"
            placeholder={t('login.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div>
          <Label htmlFor="password" className="sr-only">{t('login.password')}</Label>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            // Đăng nhập lại (email điền sẵn) → con trỏ vào luôn ô mật khẩu.
            autoFocus={!!initialEmail}
            placeholder={t('login.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        <div className="flex items-center justify-between gap-3 text-sm">
          <label className="flex cursor-pointer items-center gap-2 text-muted-foreground">
            <Checkbox checked={agreedTerms} onCheckedChange={(v) => setAgreedTerms(v === true)} aria-label={t('login.agreeAria')} />
            <span>
              <Trans
                t={t}
                i18nKey="login.agree"
                components={{
                  terms: <a href={termsUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()} />,
                  privacy: <a href={privacyUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()} />,
                }}
              />
            </span>
          </label>
          {forgotLink}
        </div>
        {error && <p className="rounded-lg bg-destructive/10 p-2.5 text-sm text-destructive" role="alert">{error}</p>}
        <TurnstileBox ts={ts} />
        <Button type="submit" className="h-12 w-full rounded-lg text-base" disabled={loading || ts.pending}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('login.submit')}
        </Button>
      </form>
      <SocialRow clientId={resolveGoogleClientId(config)} onCredential={onGoogle} />
      <Dialog open={showTermsConfirm} onOpenChange={setShowTermsConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('login.termsConfirmTitle')}</DialogTitle>
            <DialogDescription>
              <Trans
                t={t}
                i18nKey="login.termsConfirmBody"
                components={{
                  terms: <a href={termsUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline" />,
                  privacy: <a href={privacyUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline" />,
                }}
              />
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTermsConfirm(false)}>{t('common:actions.cancel')}</Button>
            <Button onClick={onConfirmTerms} disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('login.agreeAndLogin')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
